// ============================================================================
// useWorkScope — which buildings Home, My Work and Projects are looking at.
//
// Item #650, Ben 2026-09-30 (D1): three views.
//   mine    "My facilities" — account manager of record, or on my active route
//   markets "My markets"    — every building in the markets on my sales record
//   all     "All TCS"       — everything that is not closed
// Account managers start on mine, sales on markets, everyone else on all.
//
// ⛔ THE DATABASE DECIDES. Which views are offered, where a person starts and
// which buildings each covers come from get_work_scopes() and
// work_scope_facility_ids(). This hook only remembers the person's last pick
// (per person, in the browser, the way the theme and the Mine/All switch on My
// Accounts are remembered) and never works out a set itself (TCSD-0341).
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth';
import { errorMessage } from '../errors';

export type WorkScope = 'mine' | 'markets' | 'all';

export const WORK_SCOPE_LABEL: Record<WorkScope, string> = {
  mine: 'My facilities',
  markets: 'My markets',
  all: 'All TCS',
};

export interface WorkScopeOption {
  scope: WorkScope;
  label: string;
  facilityCount: number;
  isDefault: boolean;
}

const KEY = 'tcs.workScope.';

function readStored(eid: string | null): WorkScope | null {
  if (!eid) return null;
  try {
    const v = window.localStorage.getItem(KEY + eid);
    return v === 'mine' || v === 'markets' || v === 'all' ? v : null;
  } catch {
    return null;
  }
}

export interface WorkScopeState {
  scope: WorkScope | null;
  setScope: (s: WorkScope) => void;
  options: WorkScopeOption[];
  /** The buildings in the current view. null = every building (All TCS); undefined = still loading. */
  facilityIds: string[] | null | undefined;
  /** True once the view and its building list are both known. */
  ready: boolean;
  resolved: boolean;
  error: string | null;
}

export function useWorkScope(): WorkScopeState {
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const [options, setOptions] = useState<WorkScopeOption[]>([]);
  const [stored, setStored] = useState<WorkScope | null>(() => readStored(eid));
  const [facilityIds, setFacilityIds] = useState<string[] | null | undefined>(undefined);
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setStored(readStored(eid)), [eid]);

  useEffect(() => {
    if (!eid) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase.rpc('get_work_scopes');
      if (cancelled) return;
      if (e) {
        setError(errorMessage(e, 'Could not work out which buildings are yours.'));
        setOptions([{ scope: 'all', label: WORK_SCOPE_LABEL.all, facilityCount: 0, isDefault: true }]);
        setResolved(true);
        return;
      }
      setOptions(
        ((data ?? []) as { scope: WorkScope; facility_count: number; is_default: boolean }[]).map((r) => ({
          scope: r.scope,
          label: WORK_SCOPE_LABEL[r.scope] ?? r.scope,
          facilityCount: r.facility_count,
          isDefault: r.is_default,
        })),
      );
      setError(null);
      setResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [eid]);

  const scope = useMemo<WorkScope | null>(() => {
    if (!resolved) return null;
    if (stored && options.some((o) => o.scope === stored)) return stored;
    return options.find((o) => o.isDefault)?.scope ?? 'all';
  }, [resolved, stored, options]);

  // The building list for the current view, from the database.
  useEffect(() => {
    if (!scope) return;
    setFacilityIds(undefined);
    if (scope === 'all') {
      setFacilityIds(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase.rpc('work_scope_facility_ids', { p_scope: scope });
      if (cancelled) return;
      if (e) {
        setError(errorMessage(e, 'Could not load the buildings in this view.'));
        setFacilityIds([]);
        return;
      }
      setFacilityIds((data ?? []) as string[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [scope]);

  const setScope = useCallback(
    (s: WorkScope) => {
      setStored(s);
      if (!eid) return;
      try {
        window.localStorage.setItem(KEY + eid, s);
      } catch {
        /* private browsing — the pick lasts for this visit only */
      }
    },
    [eid],
  );

  const ready = !!scope && facilityIds !== undefined;
  return { scope, setScope, options, facilityIds, ready, resolved, error };
}
